// frontend/src/pages/freelancer/FreelancerServiceBulkAdd.js
import React, { useState, useEffect, useCallback } from "react";
import { prepareImageForUpload } from "../../utils/imageUploadPolicy";
import { useNavigate, useLocation } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import "./freelancerStyles.css";

function FreelancerServiceBulkAdd() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isBoothRenter, setIsBoothRenter] = useState(false);
  const isBoothRenterRoute = location.pathname.startsWith(
    "/dashboard/booth-renter",
  );
  const basePath = isBoothRenter || isBoothRenterRoute
    ? "/dashboard/booth-renter"
    : "/dashboard/freelancer";
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Initialize with 5 empty service forms
  const [services, setServices] = useState([
    {
      name: "",
      description: "",
      price: "",
      durationMinutes: "",
      category: "other",
      serviceImage: "",
      imageFile: null,
      imagePreview: null,
    },
    {
      name: "",
      description: "",
      price: "",
      durationMinutes: "",
      category: "other",
      serviceImage: "",
      imageFile: null,
      imagePreview: null,
    },
    {
      name: "",
      description: "",
      price: "",
      durationMinutes: "",
      category: "other",
      serviceImage: "",
      imageFile: null,
      imagePreview: null,
    },
    {
      name: "",
      description: "",
      price: "",
      durationMinutes: "",
      category: "other",
      serviceImage: "",
      imageFile: null,
      imagePreview: null,
    },
    {
      name: "",
      description: "",
      price: "",
      durationMinutes: "",
      category: "other",
      serviceImage: "",
      imageFile: null,
      imagePreview: null,
    },
  ]);

  const [uploadingImages, setUploadingImages] = useState(false);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  // Fetch service categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      try {
        const staffResponse = await axios.get(
          `${baseURL}/api/freelancer/profile`,
          { headers },
        );
        const staff = staffResponse.data.staff;

        // Check if user is R7 (booth renter)
        if (staff.role === "R7") {
          setIsBoothRenter(true);
        }

        // Prefer business types returned by /api/freelancer/profile.
        // For R7 this is resolved from booth-rental location on the backend.
        let businessTypes = Array.isArray(staff.businessTypes)
          ? staff.businessTypes
          : [];

        // Backward-compatible fallback for non-R7 profiles that do not include businessTypes.
        if (
          (!businessTypes || businessTypes.length === 0) &&
          staff.businessId
        ) {
          try {
            const businessResponse = await axios.get(
              `${baseURL}/api/business/${staff.businessId}`,
              { headers },
            );
            businessTypes = businessResponse.data?.type || [];
          } catch (bizErr) {
          }
        }

        const fetchByBusinessTypes = async (typeValues) => {
          if (!Array.isArray(typeValues) || typeValues.length === 0) return [];
          const businessCategoriesParam = typeValues.join(",");
          const categoriesResponse = await axios.get(
            `${baseURL}/api/platform-settings/service-categories?businessCategories=${businessCategoriesParam}`,
          );
          return categoriesResponse.data.categories || [];
        };

        let fetchedCategories = [];
        let resolvedTypes = businessTypes;

        if (businessTypes && businessTypes.length > 0) {
          fetchedCategories = await fetchByBusinessTypes(businessTypes);
        }

        // If direct business type lookup returns nothing, derive business categories from professional types.
        const professionalTypes = Array.isArray(staff.professionalTypes)
          ? staff.professionalTypes
          : [];
        if (fetchedCategories.length === 0 && professionalTypes.length > 0) {
          const professionalTypesResponse = await axios.get(
            `${baseURL}/api/platform-settings/professional-types/public`,
          );
          const professionalTypesCatalog =
            professionalTypesResponse.data.professionalTypes || [];

          const derivedTypeSet = new Set();
          professionalTypesCatalog.forEach((pt) => {
            if (
              professionalTypes.includes(pt.value) &&
              Array.isArray(pt.businessCategories)
            ) {
              pt.businessCategories.forEach((bc) => derivedTypeSet.add(bc));
            }
          });

          const derivedTypes = Array.from(derivedTypeSet);
          if (derivedTypes.length > 0) {
            fetchedCategories = await fetchByBusinessTypes(derivedTypes);
            resolvedTypes = derivedTypes;
          }
        }

        if (fetchedCategories.length > 0) {
          setServiceCategories(fetchedCategories);
          setServices((prevServices) =>
            prevServices.map((svc) => {
              const hasCurrent = fetchedCategories.some(
                (cat) => cat.value === svc.category,
              );
              return hasCurrent
                ? svc
                : { ...svc, category: fetchedCategories[0].value };
            }),
          );
        } else {
          setServiceCategories([]);
          setErrorMsg(
            "No service categories are configured for this account. Please contact support.",
          );
        }
      } catch (err) {
        setServiceCategories([]);
        setErrorMsg(
          "Unable to fetch service categories right now. Please refresh and try again.",
        );
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/templates/bulk-services-template.csv";
    link.download = "bulk-services-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleInputChange = (index, field, value) => {
    const updatedServices = [...services];
    updatedServices[index][field] = value;
    setServices(updatedServices);

    // Clear messages on input change
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleImageChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setErrorMsg(`Service ${index + 1}: Please select an image file`);
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMsg(`Service ${index + 1}: Image must be less than 5MB`);
        return;
      }

      const updatedServices = [...services];
      updatedServices[index].imageFile = file;

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const servicesWithPreview = [...updatedServices];
        servicesWithPreview[index].imagePreview = reader.result;
        setServices(servicesWithPreview);
      };
      reader.readAsDataURL(file);
      setErrorMsg("");
    }
  };

  const uploadImages = async (servicesList) => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return servicesList;

    const uploadedServices = await Promise.all(
      servicesList.map(async (svc, index) => {
        if (!svc.imageFile) return { ...svc, serviceImage: null };

        try {
          const formData = new FormData();
          formData.append("image", await prepareImageForUpload(svc.imageFile));

          const res = await axios.post(
            `${baseURL}/api/staff/services/upload-image`,
            formData,
            {
              headers: {
                ...headers,
                "Content-Type": "multipart/form-data",
              },
            },
          );

          return { ...svc, serviceImage: res.data.imageUrl };
        } catch (err) {
          throw new Error(`Failed to upload image for service ${index + 1}`);
        }
      }),
    );

    return uploadedServices;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    setSaving(true);

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) {
        setSaving(false);
        return;
      }

      // Filter out empty services (those without a name)
      const validServices = services.filter((svc) => svc.name.trim() !== "");

      if (!serviceCategories.length) {
        setErrorMsg(
          "Service categories are not loaded yet. Please refresh and try again.",
        );
        setSaving(false);
        return;
      }

      if (validServices.length === 0) {
        setErrorMsg("Please add at least one service");
        setSaving(false);
        return;
      }

      // Validate each service
      for (let i = 0; i < validServices.length; i++) {
        const svc = validServices[i];

        if (!svc.price || parseFloat(svc.price) < 0) {
          setErrorMsg(`Service ${i + 1}: Valid price is required`);
          setSaving(false);
          return;
        }

        if (!svc.durationMinutes || parseInt(svc.durationMinutes) < 5) {
          setErrorMsg(`Service ${i + 1}: Duration must be at least 5 minutes`);
          setSaving(false);
          return;
        }
      }

      // Upload all images first
      setUploadingImages(true);
      let servicesWithImages;
      try {
        servicesWithImages = await uploadImages(validServices);
      } catch (err) {
        setErrorMsg(err.message);
        setSaving(false);
        setUploadingImages(false);
        return;
      }
      setUploadingImages(false);

      // Prepare services for API
      const servicesToCreate = servicesWithImages.map((svc) => ({
        name: svc.name.trim(),
        description: svc.description.trim(),
        price: parseFloat(svc.price),
        durationMinutes: parseInt(svc.durationMinutes),
        category: svc.category,
        serviceImage: svc.serviceImage,
      }));

      // Send bulk create request
      const res = await axios.post(
        `${baseURL}/api/staff/services/bulk`,
        { services: servicesToCreate },
        { headers },
      );

      setSuccessMsg(`${res.data.count} service(s) created successfully!`);

      // Redirect back to services list after 1.5 seconds
      setTimeout(() => {
        navigate(`${basePath}/services`);
      }, 1500);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        const errorMessage =
          err?.response?.data?.message ||
          err.message ||
          "Unable to create services. Please try again.";
        setErrorMsg(errorMessage);

        // Show specific validation errors if available
        if (err?.response?.data?.errors) {
          setErrorMsg(err.response.data.errors.join("; "));
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`${basePath}/services`);
  };

  return (
    <FreelancerSidebarLayout>
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "20px" }}>
        {/* Back Button */}
        <button
          onClick={() => navigate(`${basePath}/services`)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            marginBottom: "20px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            background: "#fff",
            color: "#333",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#f5f5f5";
            e.target.style.borderColor = "#001d4a";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#fff";
            e.target.style.borderColor = "#ddd";
          }}
        >
          <span>←</span> Back to Services
        </button>

        {/* Header */}
        <div style={{ marginBottom: "30px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "15px",
            }}
          >
            <div>
              <h2 style={{ margin: "0 0 8px 0" }}>Bulk Add Services</h2>
              <p style={{ margin: 0, color: "#666" }}>
                Add up to 5 services at once. Leave fields blank to skip.
              </p>
            </div>
            <a
              href={`${process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com"}/templates/bulk-services-template.csv`}
              download="bulk-services-template.csv"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 20px",
                background: "#e6f7ff",
                border: "1px solid #001d4a",
                borderRadius: "8px",
                color: "#001d4a",
                textDecoration: "none",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
            >
              📥 Download CSV Template
            </a>
          </div>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {/* Success Message */}
        {successMsg && (
          <div
            style={{
              background: "#e6ffed",
              border: "1px solid #03543f",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#03543f" }}>{successMsg}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {services.map((service, index) => (
            <div
              key={index}
              className="dashboard-card"
              style={{
                padding: "25px",
                marginBottom: "20px",
                border: service.name
                  ? "2px solid #001d4a"
                  : "1px solid #e0e0e0",
              }}
            >
              <h4
                style={{ marginTop: 0, marginBottom: "20px", color: "#001d4a" }}
              >
                Service {index + 1}
              </h4>

              {/* Service Name */}
              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "500",
                    marginBottom: "6px",
                    color: "#001d4a",
                  }}
                >
                  Service Name{" "}
                  {index === 0 && <span style={{ color: "#c00" }}>*</span>}
                </label>
                <input
                  type="text"
                  value={service.name}
                  onChange={(e) =>
                    handleInputChange(index, "name", e.target.value)
                  }
                  maxLength={100}
                  required={index === 0}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                  placeholder="e.g., Haircut & Style"
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "500",
                    marginBottom: "6px",
                    color: "#001d4a",
                  }}
                >
                  Description
                </label>
                <textarea
                  value={service.description}
                  onChange={(e) =>
                    handleInputChange(index, "description", e.target.value)
                  }
                  maxLength={500}
                  rows={2}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                    resize: "vertical",
                  }}
                  placeholder="Describe this service..."
                />
              </div>

              {/* Price, Duration, Category */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "15px",
                  marginBottom: "15px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "500",
                      marginBottom: "6px",
                      color: "#001d4a",
                    }}
                  >
                    Price ($){" "}
                    {index === 0 && <span style={{ color: "#c00" }}>*</span>}
                  </label>
                  <input
                    type="number"
                    value={service.price}
                    onChange={(e) =>
                      handleInputChange(index, "price", e.target.value)
                    }
                    required={service.name !== ""}
                    min="0"
                    step="0.01"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "500",
                      marginBottom: "6px",
                      color: "#001d4a",
                    }}
                  >
                    Duration (min){" "}
                    {index === 0 && <span style={{ color: "#c00" }}>*</span>}
                  </label>
                  <input
                    type="number"
                    value={service.durationMinutes}
                    onChange={(e) =>
                      handleInputChange(
                        index,
                        "durationMinutes",
                        e.target.value,
                      )
                    }
                    required={service.name !== ""}
                    min="5"
                    step="5"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                    placeholder="30"
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "500",
                      marginBottom: "6px",
                      color: "#001d4a",
                    }}
                  >
                    Category
                  </label>
                  <select
                    value={service.category}
                    onChange={(e) =>
                      handleInputChange(index, "category", e.target.value)
                    }
                    disabled={
                      loadingCategories || serviceCategories.length === 0
                    }
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                      background: "#fff",
                      cursor: "pointer",
                      boxSizing: "border-box",
                    }}
                  >
                    {loadingCategories ? (
                      <option disabled>Loading...</option>
                    ) : serviceCategories.length === 0 ? (
                      <option value="" disabled>
                        No categories available
                      </option>
                    ) : (
                      serviceCategories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              {/* Image Upload - COMMENTED OUT */}
              {/* <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  marginBottom: '6px',
                  color: '#001d4a'
                }}>
                  Service Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(index, e)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    cursor: 'pointer'
                  }}
                />
                {service.imagePreview && (
                  <div style={{ marginTop: '10px' }}>
                    <img 
                      src={service.imagePreview} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: '150px', 
                        maxHeight: '100px', 
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0'
                      }}
                    />
                  </div>
                )}
              </div> */}
            </div>
          ))}

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              gap: "15px",
              justifyContent: "flex-end",
              marginTop: "30px",
            }}
          >
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              style={{
                padding: "12px 30px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                background: "#fff",
                color: "#333",
                fontSize: "16px",
                fontWeight: "500",
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || uploadingImages}
              className="btn-primary"
              style={{
                padding: "12px 30px",
                minWidth: "180px",
              }}
            >
              {uploadingImages
                ? "Uploading Images..."
                : saving
                  ? "Creating..."
                  : "Create Services"}
            </button>
          </div>
        </form>
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerServiceBulkAdd;
